/**
 * Bloom's DEMO catalog — the products and collections a grid falls back to when nothing real resolves.
 *
 * It lives in ui-react, not in the storefront app, for one reason: the BUILDER needs it too. A seller
 * opening Bloom on a store with no products used to get the house fallback — a clothing shop, "Cotton
 * Crew Tee" and stock photos of coat racks — inside a beauty theme. The theme's own world has to travel
 * with the theme (spec 0032 §3 always listed `catalog` as one of a theme's four pieces).
 *
 * TYPE-ONLY IMPORTS on purpose: this module must stay free of any section import, because
 * `sections/_shared/product.ts` reads it. Anything else here would close an import cycle.
 *
 * Prices take the STORE's currency rather than carrying their own: the same demo has to read correctly
 * in a MAD shop and in a EUR one, and a demo that shows the wrong currency teaches the seller nothing.
 */
const IMG = '/demo/beauty';
function product(currency, p) {
    return {
        id: p.id,
        name: p.name,
        slug: p.slug,
        description: p.desc,
        price: { amount: p.price, currency },
        compareAtPrice: p.compareAt ? { amount: p.compareAt, currency } : null,
        images: [{ url: `${IMG}/${p.image}`, alt: p.alt, isCover: true }],
        inventory: { tracked: false, quantity: null },
        seo: { metaTitle: null, metaDescription: null },
        status: 'active',
        tags: p.badge ? [p.badge] : [],
        vendor: 'SAHLINE',
        rating: p.rating,
        options: [
            { name: 'Format', values: (p.formats ?? ['30 ml', '50 ml']).map((label) => ({ label })) },
        ],
    };
}
/** The SAHLINE range, in merchandising order. */
export function bloomDemoProducts(currency) {
    return [
        product(currency, {
            id: 'p-sahline-serum-eclat',
            name: 'Sérum Éclat — Argan & Vitamine C',
            slug: 'serum-eclat-argan-vitamine-c',
            price: 34900,
            compareAt: 42900,
            badge: 'custom-badge-1',
            image: 'product-serum.webp',
            alt: 'Flacon compte-gouttes du Sérum Éclat',
            rating: { value: 4.8, count: 312 },
            desc: 'Unifie le teint et ravive l’éclat, sans effet peeling.',
        }),
        product(currency, {
            id: 'p-sahline-creme-hydratante',
            name: 'Crème Hydratante Jour',
            slug: 'creme-hydratante-jour',
            price: 29900,
            image: 'product-cream.webp',
            alt: 'Pot de crème hydratante',
            rating: { value: 4.9, count: 245 },
            desc: 'Une hydratation qui tient la journée, texture non grasse.',
            formats: ['50 ml'],
        }),
        product(currency, {
            id: 'p-sahline-huile-argan',
            name: 'Huile d’Argan Pure',
            slug: 'huile-argan-pure',
            price: 24900,
            compareAt: 29900,
            badge: 'custom-badge-2',
            image: 'product-oil.webp',
            alt: 'Flacon pompe d’huile d’argan',
            rating: { value: 4.9, count: 188 },
            desc: 'Pressée à froid, coopérative de Taroudant. Visage, corps et cheveux.',
        }),
        product(currency, {
            id: 'p-sahline-masque-rhassoul',
            name: 'Masque au Rhassoul',
            slug: 'masque-rhassoul',
            price: 21900,
            image: 'product-mask.webp',
            alt: 'Pot de masque au rhassoul',
            rating: { value: 4.6, count: 121 },
            desc: 'Argile de l’Atlas, deux fois par semaine, pour un grain de peau affiné.',
            formats: ['75 ml'],
        }),
        product(currency, {
            id: 'p-sahline-brume-florale',
            name: 'Brume Florale — Eau de Rose',
            slug: 'brume-florale-eau-de-rose',
            price: 15900,
            image: 'product-mist.webp',
            alt: 'Brume florale en flacon verre dépoli',
            rating: { value: 4.8, count: 203 },
            desc: 'Distillée dans la vallée du Dadès. Apaise et prépare la peau.',
            formats: ['100 ml'],
        }),
        product(currency, {
            id: 'p-sahline-contour-yeux',
            name: 'Contour des Yeux',
            slug: 'contour-des-yeux',
            price: 27900,
            image: 'product-eye.webp',
            alt: 'Petit pot de soin contour des yeux',
            rating: { value: 4.6, count: 84 },
            desc: 'Défatigue le regard et lisse les ridules de déshydratation.',
            formats: ['15 ml'],
        }),
        product(currency, {
            id: 'p-sahline-nettoyant-doux',
            name: 'Nettoyant Doux Quotidien',
            slug: 'nettoyant-doux-quotidien',
            price: 18900,
            image: 'product-cleanser.webp',
            alt: 'Tube de nettoyant doux',
            rating: { value: 4.7, count: 156 },
            desc: 'Nettoie sans décaper, même les peaux réactives.',
            formats: ['150 ml'],
        }),
        product(currency, {
            id: 'p-sahline-baume-levres',
            name: 'Baume Lèvres Nourrissant',
            slug: 'baume-levres-nourrissant',
            price: 8900,
            image: 'product-balm.webp',
            alt: 'Boîtier de baume à lèvres',
            rating: { value: 4.9, count: 267 },
            desc: 'Cire d’abeille et argan, pour des lèvres réparées la nuit.',
            formats: ['15 g'],
        }),
    ];
}
/** Bloom merchandises by CONCERN, not by product family — that is the beauty shelf. */
export function bloomDemoCollections() {
    const make = (id, name, slug, cover, alt, productCount, description) => ({
        id,
        name,
        slug,
        description,
        coverImage: { url: `${IMG}/${cover}`, alt },
        productCount,
    });
    return [
        make('c-hydratation', 'Hydratation', 'hydratation', 'need-hydration.webp', 'Crème hydratante et gouttes d’eau', 4, 'Pour les peaux qui tiraillent, dès la sortie de la douche.'),
        make('c-eclat', 'Éclat', 'eclat', 'need-glow.webp', 'Sérum éclat et agrume coupé', 3, 'Teint terne, taches, lendemains difficiles.'),
        make('c-sensible', 'Peau sensible', 'peau-sensible', 'need-sensitive.webp', 'Brume apaisante, avoine et aloès', 3, 'Rougeurs, réactivité, tout ce qui pique quand ça ne devrait pas.'),
        make('c-fermete', 'Fermeté', 'fermete', 'need-firmness.webp', 'Soin contour des yeux', 2, 'Ridules de déshydratation et perte de tonus.'),
    ];
}
//# sourceMappingURL=catalog.js.map